/**
 * Read-only access explainer for one adventure (Backlog 060 §10): why a user might not be able to start it.
 *
 * Shows the stop's own condition gate and every Story rule on it: which Story, where the rule is set (the Story,
 * a chapter or the finale), whether it is an access or a display rule, and the server's plain-words summary of
 * its criteria. Every rule must pass; testers bypass every gate. Also lists Story memberships whose access is
 * open. Renders nothing when the stop is in no Story and has no gate of its own.
 */

import { Alert, Box, Chip, CircularProgress, Link, List, ListItem, Paper, Typography } from '@mui/material'
import { LockOpen, Lock } from '@mui/icons-material'
import { useQuery } from '@tanstack/react-query'
import { storiesApi, storyPlaySiteUrl } from '@api/endpoints/stories'
import type { StoryAccessRuleDto } from '@api/types/stories'
import { errorMessage } from '@features/admin/components/users/userFormatting'
import { ruleCategoryLabel, ruleSourceLabel } from '../storyFormatting'
import { STORIES_KEY } from '../storyQueryKeys'

export function AdventureStoryAccessPanel({ adventureId }: { adventureId: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: [...STORIES_KEY, 'adventure-access', adventureId],
    queryFn: () => storiesApi.getAdventureAccess(adventureId),
    staleTime: 30_000,
  })

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, color: 'text.secondary' }}>
        <CircularProgress size={16} />
        <Typography variant="body2">Loading access</Typography>
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        Could not load access: {errorMessage(error, 'request failed')}
      </Alert>
    )
  }

  if (!data || (!data.ownConditionGate && data.rules.length === 0 && data.freeMemberships.length === 0)) return null

  // A rule whose summary is "open" lets everyone through; anything else restricts somebody.
  const gated = data.ownConditionGate || data.rules.some((r) => r.summary.trim().toLowerCase() !== 'open')

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
        {gated ? <Lock fontSize="small" color="warning" /> : <LockOpen fontSize="small" color="success" />}
        <Typography variant="subtitle2" fontWeight="bold" sx={{ flex: 1, minWidth: 0 }}>
          Access
        </Typography>
        <Chip size="small" color={gated ? 'warning' : 'success'} label={gated ? 'Gated' : 'Open'} />
      </Box>

      {data.ownConditionGate && (
        <Typography variant="body2" sx={{ mb: 1 }}>
          The adventure's own conditions require a subscription (set in its editor, not by a Story).
        </Typography>
      )}

      {data.rules.length > 0 && (
        <>
          <Typography variant="caption" color="text.secondary" component="div">
            Story rules (every rule must pass; testers bypass)
          </Typography>
          <RuleList rules={data.rules} />
        </>
      )}

      {data.freeMemberships.length > 0 && (
        <>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
            Open in
          </Typography>
          <RuleList rules={data.freeMemberships} />
        </>
      )}
    </Paper>
  )
}

function RuleList({ rules }: { rules: StoryAccessRuleDto[] }) {
  return (
    <List dense disablePadding>
      {rules.map((r) => (
        <ListItem key={`${r.collectionId}-${r.category}-${r.source}-${r.chapterId ?? ''}`} disableGutters sx={{ display: 'block' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Link href={storyPlaySiteUrl(r.collectionId)} target="_blank" rel="noopener noreferrer" variant="body2" underline="hover" sx={{ wordBreak: 'break-word' }}>
              {r.storyName}
            </Link>
            {!r.storyIsPublic && <Chip size="small" variant="outlined" label="Hidden" />}
          </Box>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ wordBreak: 'break-word' }}>
            {ruleCategoryLabel(r.category)} · {ruleSourceLabel(r)} · {r.summary}
          </Typography>
        </ListItem>
      ))}
    </List>
  )
}
